
import { Component, Prop, Vue } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import { PriceModel } from "@/dtos/priceModel";
import { Entgratart } from "@/dtos/entgratart";
import { getEntgratartText } from "@/dtos/entgratart";
import { OrderTrackingState } from "@/dtos/orderTrackingState";
import { getZincCoatingText, ZincCoating } from "@/dtos/zincCoating";

@Component
export default class OrderDetail extends Vue {
	@Prop()
	private orderId!: number;
	// Status/Tracking/Vorausrechnung werden nachgeladen (nicht per URL übergeben),
	// damit ein Direktlink auf /Orders/:orderId funktioniert.
	private state: OrderTrackingState | null = null;
	private trackingcode: string | null = null;
	private trackingLink: string | null = null;
	private prepayInvoiceId: number | null = null;
	private order: any = null;
	private loading: boolean = false;
	private certificateError: boolean = false;
	private fertiger: any = {
		companyName: "",
		name: "",
		street: "",
		zipAndTown: "",
	};

	private mounted() {
		this.loadOrderConfirmation();
		this.loadOrderTracking();
	}

	private async loadOrderTracking() {
		try {
			const response = await httpClient().get(
				`order/getOrderTracking?orderId=${this.orderId}`
			);
			const tracking = response.data;
			if (tracking) {
				this.state = tracking.state;
				this.trackingcode = tracking.trackingcode;
				this.trackingLink = tracking.translatedTrackingLink;
				this.prepayInvoiceId = tracking.prepayInvoiceId;
			}
		} catch (e) {
			console.error(e);
		}
	}

	private goBack() {
		this.$router.push({ name: "Orders" });
	}

	private get isWaitingForPayment(): boolean {
		return this.state === OrderTrackingState.CreditOverdrawn && this.prepayInvoiceId != null;
	}

	private stateLabel(state: OrderTrackingState): string {
		if (this.isWaitingForPayment) {
			return "Warten auf Zahlungseingang";
		}
		switch (state) {
			case OrderTrackingState.Ordered:
				return this.$t("orderHistory.orders.states.ordered") as string;
			case OrderTrackingState.Delivered:
				return this.$t("orderHistory.orders.states.delivered") as string;
			case OrderTrackingState.Paid:
				return this.$t("orderHistory.orders.states.paid") as string;
			case OrderTrackingState.InProduction:
				return this.$t("orderHistory.orders.states.inProduction") as string;
			case OrderTrackingState.Produced:
				return this.$t("orderHistory.orders.states.produced") as string;
			case OrderTrackingState.Fakturiert:
				return this.$t("orderHistory.orders.states.invoiced") as string;
			case OrderTrackingState.Cancelled:
				return this.$t("orderHistory.orders.states.cancelled") as string;
			case OrderTrackingState.CreditOverdrawn:
				return this.$t("orderHistory.orders.states.creditOverdrawn") as string;
			default:
				return this.$t("orderHistory.orders.states.unknown") as string;
		}
	}

	private chipClass(state: OrderTrackingState): string {
		if (this.isWaitingForPayment) {
			return "progress";
		}
		switch (state) {
			case OrderTrackingState.Ordered:
			case OrderTrackingState.InProduction:
				return "progress";
			case OrderTrackingState.Delivered:
			case OrderTrackingState.Produced:
			case OrderTrackingState.Fakturiert:
				return "transit";
			case OrderTrackingState.Paid:
				return "done";
			case OrderTrackingState.Cancelled:
			case OrderTrackingState.CreditOverdrawn:
				return "alert";
			default:
				return "settled";
		}
	}

	private get downloadableCertificates(): any[] {
		if (!this.order || !this.order.positions) {
			return [];
		}
		const stateAllowsDownload =
			this.state === OrderTrackingState.Delivered ||
			this.state === OrderTrackingState.Paid ||
			this.state === OrderTrackingState.Fakturiert;
		if (!stateAllowsDownload) {
			return [];
		}
		return this.order.positions.filter((p: any) => p.certificateTypes !== 0);
	}

	private hideBrokenThumb(event: Event) {
		const img = event.target as HTMLElement;
		if (img) {
			img.style.display = "none";
		}
	}

	private getEntgratartString(entgratart: Entgratart) {
		let text = "";
		if (entgratart != Entgratart.None) {
			text += (this.$t("orderHistory.orderDetail.deburring") as string) + " ";
		}
		text += getEntgratartText(entgratart);
		return text;
	}

	private getZincCoatingText(zincCoating: ZincCoating) {
		return getZincCoatingText(zincCoating);
	}

	private async loadOrderConfirmation() {
		try {
			this.loading = true;
			var response = await httpClient().get(
				`order/getOrderConfirmation?orderId=${this.orderId}`
			);
			this.order = response.data;
			if (this.order.rampenabholung) {
				var response = await httpClient().get(
					`order/getFertiger?fertigerId=${this.order.fertigerId}`
				);
				this.fertiger.companyName = response.data.companyname;
				this.fertiger.name = this.$t("orderHistory.orderDetail.pickupAddressName");
				this.fertiger.steet = response.data.street;
				this.fertiger.zipAndTown = `${response.data.zip} ${response.data.town}`;
			}
		} catch (e) {
			console.error(e);
		} finally {
			this.loading = false;
		}
	}

	private get priceModelText(): string {
		let text = "";
		switch (this.order.priceModel) {
			case PriceModel.Standard:
				text = this.$t("orderHistory.orderDetail.priceModel.standard") as string;
				break;
			case PriceModel.Slow:
				text = this.$t("orderHistory.orderDetail.priceModel.slow") as string;
				break;
			case PriceModel.Express:
				text = this.$t("orderHistory.orderDetail.priceModel.express") as string;
				break;
			default:
				break;
		}
		return text;
	}

	private getMwSt() {
		var total = 0;
		for (const position of this.order.positions) {
			total += position.pricePerUnit * position.quantity;
		}
		var mwst = Math.round(total * this.order.mwStSatz) / 100;
		return mwst;
	}

	private async downloadCertificate(position: any, order: any) {
		try {
			const response = await httpClient().get(
				`order/getCertificate?orderId=${order.blexonOrderId}&articleId=${position.blexonArticleId}`,
				{
					responseType: "blob",
				}
			);
			const url = URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.download = `Certificate ${order.blexonOrderId} - ${position.blexonArticleId}.pdf`;
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			this.certificateError = true;
		}
	}

	private getTotal() {
		var total = 0;
		for (const position of this.order.positions) {
			total += position.pricePerUnit * position.quantity;
		}
		return total + this.getMwSt();
	}

	private getThumbnailUrl(articleId: number) {
		return this.$store.getters.getThumbnailUrl(articleId, undefined);
	}

	private get assemblies(): { assemblyPosition: number; subarticleIds: number[] }[] {
		const assemblies = this.order.positions
			.map((p: any) => {
				if (p.includedSubarticleIds && p.includedSubarticleIds.length > 0) {
					return {
						assemblyPosition: p.position,
						subarticleIds: p.includedSubarticleIds,
					};
				}
			})
			.filter((assembly: any) => assembly);
		return assemblies;
	}

	private getPositionNumber(position: any): string {
		const assembly: { assemblyPosition: number; subarticleIds: number[] } | undefined =
			this.assemblies.filter((a: any) =>
				a.subarticleIds.includes(Number(position.blexonArticleId))
			)[0];
		return assembly ? `${assembly.assemblyPosition}.${position.position}` : position.position;
	}
}
